/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-else-return */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { Col, Image, Row, Skeleton, Tooltip } from "antd";
import { capitalize } from "lodash";
import React, { useMemo, useState } from "react";
import DocumentIcon from "../../../../icons/js/DocumentIcon";
import ImageIcon from "../../../../icons/js/ImageIcon";
import VideoIcon from "../../../../icons/js/VideoIcon";
import { formatBytes } from "../../../../library/utils";
import { heicToJpg } from "../../../../utils";
import { mediaDownloader } from "../../../../utils/mediaDownloader";
import KlipyWatermark from "../../../../icons/svg/klipy-watermark.svg";
import MediaPreview from "../../../commonComponents/MediaPreview";
import MediaPreviewTrigger from "../../../commonComponents/MediaPreviewTrigger";
import { buildMessageMediaItems } from "./mediaPreviewItems";
import "./image.css";
import MultipleMediaViewer from "./multipleMediaViewer";
import { ViewMediaPopup } from "./ViewMediaPopup";

const MultipleMediaView = props => {
  const { direction, messageDetails, media } = props;

  const [multipleFileModal, setmultipleFileModal] = useState(false);
  // Slide the shared previewer opens on; null = closed.
  const [previewIndex, setPreviewIndex] = useState(null);
  const [pdfModal, setpdfModal] = useState({
    visible: false,
    pdfUrl: "",
    mediaType: "",
  });

  // Images / videos first, in grid order, so a thumbnail's position is its slide
  // index; the documents listed underneath follow, offset by the grid length.
  //
  // Memoized because the conversation re-renders on every socket message, and
  // this would otherwise rebuild the slide list for every bubble each time.
  const { previewMedia, docMedia, previewItems } = useMemo(() => {
    const allMedia = Array.isArray(media) ? media : [];
    const inline = allMedia.filter(item => !item?.mediaType?.includes("application"));
    const docs = allMedia.filter(item => item?.mediaType?.includes("application"));
    return {
      previewMedia: inline,
      docMedia: docs,
      previewItems: buildMessageMediaItems([...inline, ...docs]),
    };
  }, [media]);

  const docSlideIndex = docIndex => previewMedia.length + docIndex;

  // Downloads keep going through mediaDownloader (proxied blob fetch + filename)
  // rather than the previewer's plain anchor. It takes the URL only — the
  // extension is derived from it — and resolves when the file has been fetched,
  // which is what drives the previewer's spinner.
  const handlePreviewDownload = item => mediaDownloader(item?.downloadUrl || item?.url);

  const handleMediaUI = mediaInfo => {
    const mediaUI = (mediaFile, index, single = false) => {
      if (mediaFile.mediaType.includes("image") || mediaFile.mediaType.includes("gif")) {
        // KLIPY-sourced GIFs require a watermark on the shared content
        // (https://docs.klipy.com/attribution). Klipy assets are served from
        // static.klipy.com, so match on the host.
        const isKlipyGif = (mediaFile?.mediaUrl || "").includes("klipy");
        return (
          <div className={`relative ${single && "text-center"}`}>
            {/* preview={false} — antd's own lightbox (and its hover mask) is
                replaced by the shared MediaPreview + MediaPreviewTrigger, opened
                at this thumbnail's slide. */}
            <MediaPreviewTrigger onPreview={() => setPreviewIndex(index)}>
              <Image
                src={heicToJpg(mediaFile.compressedMediaUrl || mediaFile.mediaUrl)}
                preview={false}
                className={`${!single && "w-full h-auto"} cursor-pointer aspect-video ${
                  single ? "object-cover w-full h-full " : "object-cover rounded"
                }`}
                alt=""
                loading="lazy"
                placeholder={<Skeleton.Image className={`${!single && "w-full h-full"}`} active />}
              />
            </MediaPreviewTrigger>
            {isKlipyGif && (
              <img
                src={KlipyWatermark}
                alt="KLIPY"
                className="absolute bottom-1 right-1 h-[16px] pointer-events-none z-10"
              />
            )}
          </div>
        );
      } else if (mediaFile.mediaType.includes("video")) {
        const mediaUrl = mediaFile?.compressedMediaUrl || mediaFile?.mediaUrl || "";
        const thumbnail = mediaFile?.compressedThumbnail || mediaFile?.thumbnailUrl;
        return (
          // clickThrough + a bottom offset keep the native control bar usable, so
          // the video still plays inline; the mask above it opens the previewer.
          <MediaPreviewTrigger
            onPreview={() => setPreviewIndex(index)}
            clickThrough
            // bottom-16 clears the full native control bar (timeline + buttons).
            maskClassName="inset-x-0 top-0 bottom-16"
          >
            <video
              src={mediaFile.compressedMediaUrl || mediaFile.mediaUrl}
              poster={thumbnail}
              // "metadata", not "none": the tile sizes itself from the video's
              // dimensions, so skipping metadata made the card widen on play.
              preload="metadata"
              className={`${!single && "w-full h-auto"} cursor-pointer aspect-video`}
              controls
            >
              <source src={mediaUrl} type="video/mp4" />
            </video>
          </MediaPreviewTrigger>
        );
      }
    };
    if (mediaInfo?.mediaType?.includes("image") || mediaInfo?.mediaType?.includes("video")) {
      const filteredMedias = previewMedia;
      return (
        <div className="relative py-2 w-full">
          {/* The grid shows up to 4 tiles; the previewer still carries every
              item, so the old hidden-image trick that fed PreviewGroup is gone. */}
          {filteredMedias.length === 1 ? (
            <div className="w-full h-full">{mediaUI(filteredMedias[0], 0, true)}</div>
          ) : filteredMedias.length === 2 ? (
            <Row gutter={[4, 4]}>
              <Col span={12}>{mediaUI(filteredMedias[0], 0)}</Col>
              <Col span={12}>{mediaUI(filteredMedias[1], 1)}</Col>
            </Row>
          ) : filteredMedias.length === 3 ? (
            <Row gutter={[4, 4]}>
              <Col span={8}>{mediaUI(filteredMedias[0], 0)}</Col>
              <Col span={8}>{mediaUI(filteredMedias[1], 1)}</Col>
              <Col span={8}>{mediaUI(filteredMedias[2], 2)}</Col>
            </Row>
          ) : filteredMedias.length > 2 ? (
            <>
              <Row gutter={[4, 4]}>
                <Col span={12}>{mediaUI(filteredMedias[0], 0)}</Col>
                <Col span={12}>{mediaUI(filteredMedias[1], 1)}</Col>
              </Row>
              <Row gutter={[4, 4]}>
                <Col span={filteredMedias.length > 2 ? 12 : 24}>
                  {mediaUI(filteredMedias[2], 2)}
                </Col>
                {filteredMedias.length > 3 && (
                  <Col span={12}>
                    <div className="relative text-center text-white cursor-pointer">
                      <div className="wrap">{mediaUI(filteredMedias[3], 3)}</div>
                      {filteredMedias.length > 4 && (
                        // Overlay counts the items past the 4th tile. Clicking it
                        // opens the previewer on the first of them.
                        <div
                          className="absolute top-[50%] left-[50%]"
                          style={{
                            transform: "translate(-50%,-50%)",
                          }}
                          onClick={() => setPreviewIndex(4)}
                        >
                          <p className="text-[20px]">+{filteredMedias.length - 3}</p>
                        </div>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </>
          ) : (
            ""
          )}
          <div className="flex-col-centered items-start mt-1 gap-2">
            {docMedia.map((item, index) => (
              <PDFRender
                key={index}
                item={item}
                index={index}
                onView={() => setPreviewIndex(docSlideIndex(index))}
              />
            ))}
          </div>
        </div>
      );
    } else {
      // Documents only — no grid above them, so a doc's slide index is its own.
      return (
        <div className="flex flex-col items-start gap-2">
          {docMedia.map((item, index) => (
            <PDFRender
              key={index}
              item={item}
              index={index}
              onView={() => setPreviewIndex(docSlideIndex(index))}
            />
          ))}
        </div>
      );
    }
  };
  return (
    <div
      className={`flex items-end ${
        direction === "TO_LEAD" ? "justify-end" : "justify-start"
      } p-0 w-full overflow-hidden`}
    >
      <div className="flex flex-col w-full">
        {media && media.length > 0 && handleMediaUI(media[0])}
      </div>
      {multipleFileModal && (
        <MultipleMediaViewer
          isModalOpen={multipleFileModal}
          handleCancel={() => setmultipleFileModal(false)}
          media={media[0]}
          allMedia={media}
          messageDetails={messageDetails}
        />
      )}
      {/* Shared previewer — replaces antd's PreviewGroup for images and the
          MediaViewer modal for videos. Mounted only while open. */}
      {previewIndex !== null && (
        <MediaPreview
          open
          items={previewItems}
          initialIndex={previewIndex}
          onDownload={handlePreviewDownload}
          onClose={() => setPreviewIndex(null)}
        />
      )}
      {pdfModal.visible && <ViewMediaPopup pdfModal={pdfModal} setpdfModal={setpdfModal} />}
    </div>
  );
};

export default MultipleMediaView;
/**
 * Filename + size row for a document (or any non-inline attachment).
 *
 * `onView` lets the caller open its own previewer for this item. Without it the
 * row keeps its original behaviour: PDFs in the ViewMediaPopup modal, other
 * types in a new tab.
 */
export function PDFRender({ index, item, onView }) {
  const [pdfModal, setpdfModal] = useState({
    visible: false,
    pdfUrl: "",
    mediaType: "",
  });
  const mediaType = item?.mediaType || "";
  const isImage = mediaType.includes("image") || mediaType.includes("gif");
  const isVideo = mediaType.includes("video");
  const FileTypeIcon = isImage ? ImageIcon : isVideo ? VideoIcon : DocumentIcon;
  const viewTooltip = isImage ? "View image" : isVideo ? "View video" : "View PDF";
  return (
    // A filename row, not a thumbnail, so hover reads as a row highlight rather
    // than the image mask — the filename itself stays the click target.
    <div
      key={index}
      className="p-3 flex-row-centered items-center gap-2 bg-white hover:bg-grey-50 transition-colors rounded-[4px] w-full"
    >
      <FileTypeIcon />
      <div className="flex-col-centered items-start gap-1 min-w-0 flex-1">
        <Tooltip title={viewTooltip}>
          <span
            onClick={() => {
              if (onView) {
                onView(item);
              } else if (item.mediaType.split("/")[1] === "pdf") {
                setpdfModal({
                  visible: true,
                  pdfUrl: item.mediaUrl,
                  mediaType: item.mediaType.split("/")[1],
                });
              } else if (isImage || isVideo) {
                window.open(item.mediaUrl, "_blank", "noopener,noreferrer");
              }
            }}
            className="text-sm text-grey-700 font-[400] cursor-pointer truncate max-w-full"
          >
            {decodeURIComponent(
              (item?.mediaUrl || "")?.substring(item?.mediaUrl?.lastIndexOf("/") + 1)
            )}
          </span>
        </Tooltip>
        <span className="flex-row-centered gap-[9px] whitespace-nowrap">
          <span className="text-xs font-[400] text-grey-500">{formatBytes(item.size)}</span>
          <div className="w-[1px] h-2.5 bg-grey-500" />
          <Tooltip title={`Download ${capitalize(item?.mediaType?.split("/")[1])}`}>
            <span
              onClick={() => mediaDownloader(item.mediaUrl, item?.mediaType?.split("/")[1])}
              className="text-xs font-[600] text-brand-500 cursor-pointer"
            >
              Download
            </span>
          </Tooltip>
        </span>
      </div>
      {pdfModal.visible && <ViewMediaPopup pdfModal={pdfModal} setpdfModal={setpdfModal} />}
    </div>
  );
}
